<template>
  <b-container fluid class="top-padding bottom-padding no-horiz-padding">

    <h2>Help with Handweaving.net</h2>

    <img style='float: left; max-width: 250px' class='right-padding bottom-padding' :src='this.getSiteImageUrl("PriscillaLace.jpg")' />

    <p>
      Below you will find answers to frequently asked questions about Handweaving.net.
    </p>

    <p>
      Please let us know using our
      <router-link to='/contact'>contact</router-link> page if you still need help with something after reading this.
    </p>

    <h3 class='top-padding' style="clear:both">Subscriptions and Full Access to Drafts</h3>

    <p>
      If you have subscribed but do not see full drafts when you visit the site, you may need to use the links in the top menu to sign out and then to sign in again.  Please <router-link to='/contact'>contact us</router-link> after you try this if it does not resolve the problem.
    </p>

    <h3 style="clear:both">Accounts and Signing In</h3>

    <p>
       If you may have forgotten your password, try the last one you remember.  The login page will display a button to help you recover it provided you still remember the email address used to sign up.
       Please <router-link to='/contact'>contact us</router-link> if you try this and it does not resolve the problem; we can locate your account to change the password and/or email for you if needed.
    </p>
    
    <h3>Clipboard</h3>
    <p>
      Everyone with an account has a private draft clipboard where you can store interesting drafts that you want to locate again.
    </p>
    
    <ul>
      <li>You must be signed in to use your draft clipboard.</li>
      <li>To add drafts to your clipboard, choose Actions -> Add to Board -> Clipboard from any draft detail page.</li>
      <li>To see your clipboard later and view or remove drafts from it, choose Account -> My Draft Boards from the menu.</li>
    </ul>
    
    <h3>Draft Boards</h3>
    <p>
        You can create any number of Draft Boards, these are public collections with titles where you can store interesting drafts for both you and others to see.
    </p>
    <ul>
      <li>You must be signed in to create draft boards and to add drafts to them.</li>
      <li>
        To add a draft board, choose Account -> My Draft Boards from the menu, enter a title, and then click Add Board.
      </li>

      <li>
        To add drafts to a board, choose choose Actions -> Add to Board -> (Board Name) from any draft detail page.
      </li>

      <li>
        To see your draft boards later, choose Account -> My Draft Boards from the menu.
      </li>

      <li>
        To see draft boards created by others, choose Drafts -> Search For Drafts -> Searcy by Board from the menu.
      </li>

      <li>Your name is shown along with any draft boards you create.</li>
    </ul>

    <h3>Draft Tags</h3>
    <p>
      Tags are descriptive words or short phrases that you can attach to any draft.  Later both you and others can search by these to find the drafts.  These can reflective of any way that you think about a draft, such as:  twill, satin, stars, color and weave, hearts, etc.
    </p>
    <ul>
      <li>You must be signed in to add tags to drafts.</li>
      <li>
        To add a tag to a draft, choose Tags from any draft detail page, enter a tag name, and click Add Tag.
      </li>

      <li>
        To see all of your draft tags, choose Account -> My Draft Tags from the menu, clicking any tag to see the associated drafts.
      </li>

      <li>
        To see all draft tags on the site, choose Drafts -> Search For Drafts -> Search By Tag from the menu, clicking any tag to see the associated drafts.
      </li>
      <li>
        Your name may be shown along with any draft tags you create (although the site does not do this now).
      </li>
    </ul>

    <h3>Draft Comments</h3>
    <p>
      You can add comments to any draft for all to see, and your name and the date are shown with these.
    </p>
    <ul>
      <li>You must be signed in to add comments to drafts.</li>
      
      <li>
        To add a comment to a draft, choose Comments from any draft detail page, enter a comment, and click Add Comment.
      </li>
      <li>
        Your name and the date/time are shown along with any comments you enter.
      </li>
      <li>
        To see all of the comments for a draft, choose Comments from any draft detail page.
      </li>

      <li>
        To delete a comment you have made on a draft, choose Comments from any draft detail page and then click the (X) icon next to the comment.
      </li>
    </ul>

    <h3>Draft Likes</h3>
    <p>
      You can Like any draft as a quick way to mark it as interesting for you and others.  Later this helps with searching and presenting drafts that are interesting or useful.
    </p>
    <ul>
      <li>You must be signed in to Like drafts.</li>
      <li>
        To Like any draft, simply click the Like button on the draft detail page.
      </li>
      <li>
        To remove a Like, simply click the Like button on the draft detail page a second time.
      </li>
      <li>
        To see all of the drafts you have Liked, choose Account -> My Liked Drafts from the menu.
      </li>
      <li>
        To see all of the drafts anyone has Liked, choose Drafts -> Search For Drafts -> Liked Drafts from the menu.
      </li>
      <li>
        Your name may be shown along with any draft likes you enter (although the site does not do this now).
      </li>
    </ul>  

    <h3>Obtaining Weaving Software</h3>
    <p>
      Our
      <router-link to='/weaving-software'>weaving software</router-link> page has information about some weaving software that supports the WIF format used by this site for draft downloads. Many of these packages have demonstration versions that can be downloaded free of charge.
    </p>
    <p>
      Drafts on the site can be viewed, printed, and used with out any weaving software; you only need a weaving software package if you want to work with WIF files for drafts downloaded from the site.
    </p>

    <h3>WIF Compatibility</h3>

    <p>
      A few people have reported problems in opening some of our WIF files with certain weaving software packages. The following steps should help resolve this.
    </p>

    <ul>
      <li>
        Make sure the name of the file you downloaded ends with
        <b>.wif</b> and not something else. Some browsers, especially on Macintosh computers, ignore the name provided by the website for saving a downloaded file and instead give the file their own incorrect name. Rename it yourself if necessary and then try opening it again.
      </li>
    </ul>

    <ul>
      <li>
        If your weaving software doesn't automatically start and load WIF files when you click on them, try opening your weaving software first, selecting its menu item for opening files, and then browsing to the WIF file that you want to open.
      </li>
    </ul>

    <ul>
      <li>
        Get the latest version of the weaving software package. The latest versions of all the programs we tried are better in general at loading WIFs than earlier versions.
      </li>
    </ul>

    <ul>
      <li>Try some additional patterns to see if the problem is specific to the one you are trying to open.</li>
    </ul>

    <ul>
      <li>
        If it still doesn't work after trying all of this, contact the company that made your weaving software. You can also send us the name and version numbers for your weaving software and your computer operating system, as well as the name of the WIF file from our site that you are trying to open and any error messages. Example: 8199.wif. We will attempt to contact the manufacturer to ask what is wrong but it may take longer than if you do this yourself.
      </li>
    </ul>

    <h3>WIF Downloading with Windows</h3>

    <p>
      Some Windows systems are configured to open WIF files automatically with a built-in program for viewing images that is not compatible with WIFs for weaving. When downloading, if you get an error message like "This document's format is invalid or not supported.", you have this problem. Fortunately, this is easy to fix. Try the following steps:
    </p>

    <ul>
      <li>Open Windows Explorer by selecting My Computer.</li>
    </ul>

    <ul>
      <li>Click on the Tools menu and choose Folder Options...</li>
    </ul>

    <ul>
      <li>Click the File Types tab.</li>
    </ul>

    <ul>
      <li>Find WIF in the list of registered file types that are shown, select it by clicking on it, and hit the Delete button. This will remove the incorrect link between WIF files and the Microsoft Imaging Preview program, which is causing your downloads to fail.</li>
    </ul>

    <ul>
      <li>Reboot the computer, after which downloads should work.</li>
    </ul>

    <h3 style="clear:both">Browser Compatibility</h3>

    <p>
      The current version of Handweaving.net was launched in early 2020 and
      <strong>requires a fairly recent web browser</strong>, generally one that was released in 2015 or later.
    </p>

    <p>
      If you are having trouble viewing any of the site pages, try updating your web browser; recent versions of the following browsers should work:
    </p>

    <ul>
      <li>Safari</li>
      <li>Chrome</li>
      <li>Firefox</li>
      <li>Opera (requires version 15 or later)</li>
      <li>Internet Explorer (requires version 10 or later)</li>
      <li>Android</li>
      <li>Chrome Mobile</li>
      <li>iOS Safari</li>
    </ul>


    <helpUsButton/>
    <mailChimpSignup/>
  </b-container>

</template>

<script>
import mailChimpSignup from './MailChimpSignup.vue'
import helpUsButton from './HelpUsButton.vue'
import mediaService from '../mixins/mediaService.js'
export default {
    mixins: [mediaService],
    components: {
        helpUsButton: helpUsButton,
        mailChimpSignup: mailChimpSignup
    },
    created() {
        this.$store.commit('setPageTitle', 'Help')
    }
}
</script>

<style scoped>
.maxLogoHeight {
    max-height: 90px;
    height: 90px;
}
</style>



// WEBPACK FOOTER //
// src/components/Help.vue